import { PublishIcon } from "@minket/brand/icons/publish";
import { TemplatesIcon } from "@minket/brand/icons/templates";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { UnavailableAction } from "@/components/page/action";
import { GhostSections } from "@/components/page/ghost";
import { DetailPage } from "@/components/page/item";
import { Page } from "@/components/page/page";
import { LoadingState } from "@/components/page/states";
import { endpoints } from "@/lib/endpoints";
import { apiQuery } from "@/lib/query";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/templates/$id")({
  component: TemplateDetail,
});

// The editor is the one screen exempt from the route budget; it loads once the template has.
const TemplateEditor = lazy(() =>
  import("@/components/templates/block-editor").then((m) => ({ default: m.TemplateEditor })),
);

const sections = [
  { title: "Details", description: "" },
  { title: "Content", description: "The blocks, or the uploaded HTML.", wide: true },
  { title: "Variables", description: "" },
  { title: "Versions", description: "" },
  { title: "Preview", description: "A version on paper, with sample values.", wide: true },
] as const;

function TemplateDetail() {
  const { id } = Route.useParams();
  const { project } = useWorkspace();
  const template = useQuery({
    ...apiQuery(endpoints.template, { projectId: project?.id ?? "", templateId: id }),
    enabled: project !== null,
  });

  if (project && template.isSuccess) {
    return (
      <Suspense
        fallback={
          <Page
            title={template.data.name}
            documentTitle={`${template.data.name} · Templates`}
            serial={{ id, label: "template id" }}
            back={{ to: "/templates", label: "Templates" }}
            action={
              <UnavailableAction label="Publish" icon={PublishIcon} reason="Loading the editor" />
            }
          >
            <LoadingState label="Loading the editor">
              <GhostSections sections={sections} busy />
            </LoadingState>
          </Page>
        }
      >
        <TemplateEditor key={template.data.id} projectId={project.id} template={template.data} />
      </Suspense>
    );
  }

  return (
    <DetailPage
      kind="Template"
      icon={TemplatesIcon}
      id={id}
      back={{ to: "/templates", label: "Templates" }}
      action={{ label: "Publish", icon: PublishIcon }}
      noun="this template"
      will="This page will hold the template's content, its variables and versions, a preview and a test send."
      sections={sections}
      query={(projectId) => apiQuery(endpoints.template, { projectId, templateId: id })}
    />
  );
}
